import { defineStore } from "pinia";

import type { State } from "./Models/State";
import type { QuestionnaireUi } from "./Models/ui/QuestionnaireUi";
import type { Questionnaire, QuestionnaireSummary } from "@/Modules/Models/Domain/Questionnaire";
import type { Customer } from "@/Modules/Models/Domain/Customer";
import { QuestionnaireTrigger, QuestionnaireType } from "@/Modules/Models/Domain/Questionnaire";

import { config } from "@/Common/envConfig";

import { useUserStore } from "@/Modules/User/Store";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import FetchQuestionnaireSummariesActionHandler from "@/Modules/Questionnaire/ActionHandlers/FetchQuestionnaireSummaries";
import FetchQuestionnaireActionHandler from "@/Modules/Questionnaire/ActionHandlers/FetchQuestionnaire";
import FetchAvailableQuestionnairesActionHandler from "@/Modules/Questionnaire/ActionHandlers/FetchAvailableQuestionnaires";
import CreateDeleteAndUnsubscribeQuestionnaire from "@/Modules/Questionnaire/ActionHandlers/CreateDeleteAndUnsubscribeQuestionnaire";
import FetchCustomerQuestionnairesActionHandler from "@/Modules/Questionnaire/ActionHandlers/FetchCustomerQuestionnaires";
import SendQuestionnaireActionHandler from "@/Modules/Questionnaire/ActionHandlers/SendQuestionnaire";
import FetchAnswerQuestionnaireActionHandler from "@/Modules/Questionnaire/ActionHandlers/FetchAnswersQuestionnaire";

import { hasValue } from "@/Utils/Objects";
import { dateToDateUi } from "@/Utils/Date";

const questionnaireState: State = {
  allQuestionnaires: [],
  questionnaires: [],
  questionnaireSummaries: [],
  deleteAndUnsubscribeQuestionnaires: [],
  reassignQuestionnaires: [],
  customerQuestionnaires: [],
  customerAnswers: {},
  currentCustomerQuestionnaireTitle: "",
  unsubscribeFormsIds: config.unsubscribeForms,
  reassignFormsIds: config.reassignForms,
  deleteFormsIds: config.deleteAccountForms,
  isLoading: false,
  showQuestionnaireModal: false,
  questionnaire: undefined,
};

export const useQuestionnaireStore = defineStore(StateNamespace.QUESTIONNAIRE, {
  state: () => ({ ...questionnaireState }),
  getters: {
    getQuestionnairesUi(questionnaireState: State): QuestionnaireUi[] {
      const questionnairesUi: QuestionnaireUi[] = [];

      for (const questionnaireSummary of questionnaireState.questionnaireSummaries) {
        const questionnaire = questionnaireState.questionnaires.find(
          (q) => q.id === questionnaireSummary.questionnaireId
        );

        if (hasValue(questionnaire)) {
          questionnairesUi.push({
            createdAt: dateToDateUi(questionnaireSummary.updatedAt || questionnaireSummary.createdAt),
            id: questionnaire.id,
            isCompleted: questionnaireSummary.isCompleted,
            name: questionnaire.name,
            visible: questionnaire.isVisible,
            renderUrl: questionnaireSummary.renderUrl,
          });
        }
      }

      return questionnairesUi;
    },

    getUncompletedQuestionnairesAmount(questionnaireState: State): number {
      return questionnaireState.questionnaireSummaries.filter(
        (questionnaireSummary) => !questionnaireSummary.isCompleted && questionnaireSummary.visible
      ).length;
    },

    getCompletedQuestionnairesAmount(questionnaireState: State): number {
      return questionnaireState.questionnaireSummaries.filter(
        (questionnaireSummary) => questionnaireSummary.isCompleted && questionnaireSummary.visible
      ).length;
    },

    getUnsubscribeQuestionnaire(questionnaireState: State) {
      const userStore = useUserStore();

      if (hasValue(userStore.user)) {
        const userLanguage: string | undefined = userStore.user?.language;
        const unsubscribeQuestionnaireId = questionnaireState.unsubscribeFormsIds[userLanguage];

        return questionnaireState.deleteAndUnsubscribeQuestionnaires.filter((quiz) => {
          return quiz.questionnaireId === unsubscribeQuestionnaireId && !quiz.isCompleted;
        });
      }
    },

    getQuestionnairesById(questionnaireState: State): (questionnaireId: string) => QuestionnaireSummary[] {
      return (questionnaireId: string): QuestionnaireSummary[] =>
        questionnaireState.questionnaireSummaries.filter(
          (quizz) => quizz.questionnaireId === questionnaireId
        );
    },

    getReassignQuestionnaire(questionnaireState: State) {
      const userStore = useUserStore();

      if (hasValue(userStore.user)) {
        const userLanguage: string | undefined = userStore.user?.language;
        const reassignQuestionnaireId = questionnaireState.reassignFormsIds[userLanguage];

        return questionnaireState.questionnaireSummaries.filter((quiz) => {
          return quiz.questionnaireId === reassignQuestionnaireId && !quiz.isCompleted;
        });
      }
    },

    getTotalPendingNotifications(): number {
      return this.getUncompletedQuestionnairesAmount;
    },

    getUnsubscribeFormId(questionnaireState: State): string | undefined {
      const userStore = useUserStore();

      if (hasValue(userStore.user)) {
        const userLanguage: string | undefined = userStore.user?.language;

        return questionnaireState.unsubscribeFormsIds[userLanguage];
      }
    },

    getDeleteFormId(questionnaireState: State): string | undefined {
      const userStore = useUserStore();

      if (hasValue(userStore.user)) {
        const userLanguage: string | undefined = userStore.user?.language;

        return questionnaireState.deleteFormsIds[userLanguage];
      }
    },

    getReassignFormId(questionnaireState: State): string | undefined {
      const userStore = useUserStore();

      if (hasValue(userStore.user)) {
        const userLanguage: string | undefined = userStore.user?.language;

        return questionnaireState.reassignFormsIds[userLanguage];
      }
    },

    customerQuestionnairesList(questionnaireState: State): QuestionnaireUi[] | [] {
      return questionnaireState.customerQuestionnaires.map((quiz) => {
        const questionnaireData = questionnaireState.allQuestionnaires.find(
          (q) => q.id === quiz.questionnaireId
        );

        if (questionnaireData) {
          return {
            isCompleted: quiz.isCompleted,
            name: questionnaireData.name,
            createdAt: dateToDateUi(quiz.createdAt),
            renderUrl: questionnaireData.renderUrl,
            id: quiz.id,
          };
        } else {
          return {
            isCompleted: quiz.isCompleted,
            name: "",
            createdAt: dateToDateUi(quiz.createdAt),
            renderUrl: "",
            id: quiz.id,
          };
        }
      });
    },

    customerQuestionnairesToSend(questionnaireState: State) {
      return questionnaireState.allQuestionnaires.filter((quiz) =>
        quiz.triggersId.find(
          (trigger) =>
            trigger.triggerId === QuestionnaireTrigger.TRIGGER_CUSTOMER_ABOUT_HIMSELF ||
            trigger.triggerId === QuestionnaireTrigger.TRIGGER_REASSIGN_REQUEST
        )
      );
    },

    customerQuestionnairesSent(questionnaireState: State): Questionnaire[] {
      return questionnaireState.allQuestionnaires
        .filter((quiz) => {
          return questionnaireState.customerQuestionnaires.find(
            (q) => quiz.id === q.questionnaireId && q.type !== QuestionnaireType.CUSTOMER_ABOUT_DELETE
          );
        })
        .map((quiz) => {
          const quizCreationDate = questionnaireState.customerQuestionnaires.find(
            (q) => quiz.id === q.questionnaireId && q.type !== QuestionnaireType.CUSTOMER_ABOUT_DELETE
          )?.createdAt;

          return {
            ...quiz,
            createdAt: quizCreationDate,
          };
        });
    },

    customerQuestionnairesCompleted(questionnaireState: State) {
      return questionnaireState.customerQuestionnaires.filter((quiz) => quiz.isCompleted).length;
    },

    customerQuestionnairesPending(questionnaireState: State) {
      return questionnaireState.customerQuestionnaires.filter((quiz) => !quiz.isCompleted).length;
    },

    questionnaireRenderUrl(state: State): string | undefined {
      if (!hasValue(state.questionnaire)) return undefined;

      const summaryQ = this.getQuestionnairesUi.find((q) => state.questionnaire!.id === q.id);

      if (!hasValue(summaryQ)) return undefined;

      return summaryQ.renderUrl;
    },
  },
  actions: {
    async fetchQuestionnaireSummaries(): Promise<void> {
      const fetchQuestionnaireSummariesActionHandler = new FetchQuestionnaireSummariesActionHandler(this);

      await fetchQuestionnaireSummariesActionHandler.handle();
    },

    async fetchQuestionnaires(): Promise<void> {
      this.isLoading = true;
      const fetchQuestionnaireActionHandler = new FetchQuestionnaireActionHandler(this);

      for (const questionnaireSummary of this.questionnaireSummaries) {
        await fetchQuestionnaireActionHandler.handle(questionnaireSummary.questionnaireId);
      }

      this.isLoading = false;
    },

    async fetchAvailableQuestionnaires(): Promise<void> {
      const fetchAvailableQuestionnairesActionHandler = new FetchAvailableQuestionnairesActionHandler(this);

      await fetchAvailableQuestionnairesActionHandler.handle();
    },

    async fetchInitial(): Promise<void> {
      const userStore = useUserStore();

      if (userStore.getUserIsTherapist) {
        await this.fetchAvailableQuestionnaires();
      }

      await this.fetchQuestionnaireSummaries();
      await this.fetchQuestionnaires();
    },

    fetchCustomerQuestionnaires(customerUuid: string): Promise<QuestionnaireSummary[]> {
      const fetchCustomerQuestionnairesActionHandler = new FetchCustomerQuestionnairesActionHandler(this);

      return fetchCustomerQuestionnairesActionHandler.handle(customerUuid);
    },

    clearCustomer() {
      this.customerQuestionnaires = [];
      this.customerAnswers = {};
    },

    async fetchAnswersQuestionnaires(quizId: string) {
      const fetchAnswerQuestionnaireActionHandler = new FetchAnswerQuestionnaireActionHandler(this);

      await fetchAnswerQuestionnaireActionHandler.handle(quizId);
    },

    async fetchUnsubscribeQuestionnaire(): Promise<QuestionnaireSummary | undefined> {
      const unsubscribeQuestionnaireId = this.getUnsubscribeFormId;
      const createDeleteAndUnsubscribeQuestionnaire = new CreateDeleteAndUnsubscribeQuestionnaire(this);
      const userStore = useUserStore();

      if (userStore.user && unsubscribeQuestionnaireId) {
        const userUuid = userStore.user.id;

        return createDeleteAndUnsubscribeQuestionnaire.handle(userUuid, unsubscribeQuestionnaireId);
      }
    },

    async createOrGetDeleteQuestionnaire(): Promise<QuestionnaireSummary | undefined> {
      const deleteQuestionnaireId = this.getDeleteFormId;
      const createDeleteAndUnsubscribeQuestionnaire = new CreateDeleteAndUnsubscribeQuestionnaire(this);
      const userStore = useUserStore();

      if (userStore.user && deleteQuestionnaireId) {
        const userUuid = userStore.user.id;

        return createDeleteAndUnsubscribeQuestionnaire.handle(userUuid, deleteQuestionnaireId);
      }
    },

    async sendQuestionnaire(user: Customer, questionnaire: Questionnaire) {
      const sendQuestionnaireActionHandler = new SendQuestionnaireActionHandler(this);

      return sendQuestionnaireActionHandler.handle(user, questionnaire);
    },

    openQuestionnaire(questionnaire: Questionnaire, onComplete?: (q: Questionnaire) => void) {
      this.questionnaire = questionnaire;
      this.showQuestionnaireModal = true;
      this.questionnaireCompleteCallback = onComplete;
    },

    completeQuestionnaire() {
      if (hasValue(this.questionnaireCompleteCallback)) {
        this.questionnaireCompleteCallback(this.questionnaire!);
      }

      this.closeQuestionnaire();
    },

    closeQuestionnaire() {
      this.questionnaire = undefined;
      this.showQuestionnaireModal = false;
      this.questionnaireCompleteCallback = undefined;
    },
  },
});

export type QuestionnaireStore = ReturnType<typeof useQuestionnaireStore>;

export default useQuestionnaireStore;
